import React, { useState, useRef, useEffect } from 'react'
import { useFormSteps, Answer } from '../../hooks/useFormSteps'
import { useFormData } from '../../hooks/useFormData'
import eligibilityFlowData from '../../data/flows/military-hearing-loss-eligibility.json'
import { FormFlow, FormStep } from '../../hooks/useFormSteps'
import BackButton from '../buttons/BackButton'
import ClaimForm from '../ClaimForm'
import ResultCard from '../ui/ResultCard'
import Spacer from '../ui/Spacer'
import FormTemplate from '../FormTemplate'
import { useStepTransition } from '../../hooks/useStepTransition'
import { JOURNEY_PROGRESS } from '../ui/JourneyProgress'
import { Heading } from '../ui/Text'
import styles from './styles.module.scss'

type ViewType = 'eligibility' | 'claim'

// How long the tapped answer stays on screen before the slide
const SELECT_HOLD_MS = 400

interface EligibilityFlowProps {
    // Tells the page when the visitor moves into (or back out of) the claim form
    onClaimViewChange?: (inClaim: boolean) => void
    // Tells the page once they've answered the first question (false again after Start over)
    onStartedChange?: (started: boolean) => void
}

const EligibilityFlow: React.FC<EligibilityFlowProps> = ({ onClaimViewChange, onStartedChange }) => {
    const [view, setView] = useState<ViewType>('eligibility')

    useEffect(() => {
        onClaimViewChange?.(view === 'claim')
    }, [view])
    const [resultMessage, setResultMessage] = useState<any>(null)
    const eligibilityRef = useRef<HTMLDivElement>(null)

    const flow = eligibilityFlowData as FormFlow
    const { currentStep, goNext, goBack, isFirstStep, reset } = useFormSteps(flow)
    const {
        formData,
        handleFieldChange,
        saveDateOfBirth,
        showDateErrors,
        triggerDateErrors,
        saveEmail,
        showEmailErrors,
        triggerEmailErrors,
        savePhone,
        showPhoneErrors,
        triggerPhoneErrors,
        saveAddress,
        setServiceField,
        setSigData,
        setSignatureConsentChecked,
        addService,
        removeService,
        showServiceDateErrors,
        triggerServiceDateErrors,
        showServiceNumberErrors,
        triggerServiceNumberErrors,
        resetFormData,
        submitFormData,
        previewFormDocuments,
    } = useFormData()
    // Answering the first question starts the journey; Start over resets it
    const started = formData.servedUkArmedForces !== null
    useEffect(() => {
        onStartedChange?.(started)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [started])

    const currentStepIndex = currentStep ? flow.steps.findIndex((step: FormStep) => step.id === currentStep.id) + 1 : 0

    const selectedAnswer = (() => {
        if (!currentStep) return null
        const value = formData[currentStep.id as keyof typeof formData]
        if (value === true) return 'yes'
        if (value === false) return 'no'
        return null
    })()

    const { transition, stepClassName, isTransitioning } = useStepTransition()

    const handleAnswerSelect = (answerValue: string) => {
        if (isTransitioning()) return

        handleFieldChange(currentStep?.id as keyof typeof formData, answerValue)
        const answer = currentStep?.answers.find((a: Answer) => a.value === answerValue)

        // Hold the tapped answer on screen briefly, then slide to what comes next
        transition(
            'forward',
            () => {
                if (answer?.success) {
                    setView('claim')
                } else if (answer?.failure) {
                    setResultMessage({ type: 'failure', ...answer.failure })
                } else if (answer?.next) {
                    goNext(answerValue)
                }
            },
            SELECT_HOLD_MS
        )
    }

    const handleQuestionBack = () => {
        transition('back', () => {
            goBack()
        })
    }

    const handleStartClaim = () => {
        setView('claim')
    }

    const handleClaimBack = () => {
        setView('eligibility')
        // Scroll to top when returning to eligibility questions
        setTimeout(() => {
            if (eligibilityRef.current) {
                eligibilityRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
        }, 0)
    }

    const handleClaimComplete = (data: any) => {
        console.log('Claim form submitted:', data)
        // Handle form submission here
    }

    const handleReset = () => {
        reset()
        setResultMessage(null)
        setView('eligibility')
        resetFormData()
    }

    const handleFullReset = () => {
        handleReset()
    }

    if (!currentStep) {
        return <div>Loading...</div>
    }

    if (view === 'claim') {
        return (
            <>
                <ClaimForm
                    formData={formData}
                    onFieldChange={handleFieldChange}
                    saveDateOfBirth={saveDateOfBirth}
                    showDateErrors={showDateErrors}
                    triggerDateErrors={triggerDateErrors}
                    saveEmail={saveEmail}
                    showEmailErrors={showEmailErrors}
                    triggerEmailErrors={triggerEmailErrors}
                    savePhone={savePhone}
                    showPhoneErrors={showPhoneErrors}
                    triggerPhoneErrors={triggerPhoneErrors}
                    saveAddress={saveAddress}
                    setServiceField={setServiceField}
                    setSigData={setSigData}
                    setSignatureConsentChecked={setSignatureConsentChecked}
                    addService={addService}
                    removeService={removeService}
                    showServiceDateErrors={showServiceDateErrors}
                    triggerServiceDateErrors={triggerServiceDateErrors}
                    showServiceNumberErrors={showServiceNumberErrors}
                    triggerServiceNumberErrors={triggerServiceNumberErrors}
                    submitFormData={submitFormData}
                    previewFormDocuments={previewFormDocuments}
                    onComplete={handleClaimComplete}
                    onBack={handleClaimBack}
                    onReset={handleFullReset}
                    resetFormData={resetFormData}
                />
            </>
        )
    }

    if (resultMessage) {
        return (
            <>
                <ResultCard resultMessage={resultMessage} onStartClaim={handleStartClaim} onReset={handleReset} />
            </>
        )
    }

    return (
        <>
            <div ref={eligibilityRef}>
                <FormTemplate
                    heading="ELIGIBILITY CHECK"
                    progress={JOURNEY_PROGRESS.eligibility[currentStepIndex - 1]}
                    currentStep={1}
                >
                    <div
                        key={currentStep.id}
                        className={`${styles.questionStep} ${stepClassName}`}
                    >
                        <Heading>{currentStep.question}</Heading>
                        <Spacer height={36} />

                        <div className={styles.answersContainer}>
                            {currentStep.answers.map((answer: Answer) => (
                                <button
                                    key={answer.value}
                                    className={`${styles.answerButton} ${selectedAnswer === answer.value ? styles.selected : ''}`}
                                    onClick={() => handleAnswerSelect(answer.value)}
                                >
                                    {answer.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {!isFirstStep && (
                        <div className={styles.navigation}>
                            <BackButton onClick={handleQuestionBack} />
                            <div className={styles.navigationSpacer}></div>
                        </div>
                    )}

                </FormTemplate>
            </div>
        </>
    )
}

export default EligibilityFlow
